import { db } from './config';
import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';

// Generate a random uppercase promo code
const generateCode = (length = 10) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export async function createPromoCode({ code, credits, maxRedemptions = 1, count, expiresAt = null, active = true, metadata = {} }, adminUserId) {
  const normalized = (code || generateCode()).toUpperCase();
  const promoRef = doc(db, 'promoCodes', normalized);

  const maxUses = Number(typeof count === 'number' ? count : maxRedemptions) || 0; // 0 means unlimited

  const payload = {
    code: normalized,
    credits: Number(credits) || 0,
    maxRedemptions: maxUses,
    redemptionCount: 0,
    active: !!active,
    createdAt: serverTimestamp(),
    createdBy: adminUserId || null,
    metadata: metadata || {}
  };

  if (expiresAt instanceof Date) {
    payload.expiresAtMs = expiresAt.getTime();
  } else if (typeof expiresAt === 'number') {
    payload.expiresAtMs = expiresAt;
  }

  const existing = await getDoc(promoRef);
  if (existing.exists()) {
    const existingData = existing.data();
    // Preserve existing redemptionCount when updating
    await setDoc(
      promoRef,
      { redemptionCount: existingData.redemptionCount || 0, ...existingData, ...payload },
      { merge: true }
    );
  } else {
    await setDoc(promoRef, payload);
  }
  return { success: true, code: normalized };
}

export async function listPromoCodes(limitCount = 50) {
  const snap = await getDocs(collection(db, 'promoCodes'));
  const items = [];
  snap.forEach(d => items.push({ id: d.id, ...d.data() }));
  items.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  return items.slice(0, limitCount);
}

export async function getPromoCode(code) {
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized) return null;
  const ref = doc(db, 'promoCodes', normalized);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function hasUserRedeemedCode(userId, code) {
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized || !userId) return false;
  const redemptionRef = doc(db, `promoCodes/${normalized}/redemptions`, userId);
  const redemptionSnap = await getDoc(redemptionRef);
  return redemptionSnap.exists();
}

export async function redeemPromoCode(userId, code) {
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized) throw new Error('Enter a promo code');
  if (!userId) throw new Error('You must be logged in');

  const promoRef = doc(db, 'promoCodes', normalized);
  const redemptionRef = doc(db, `promoCodes/${normalized}/redemptions`, userId);
  const userRef = doc(db, 'users', userId);

  return await runTransaction(db, async (tx) => {
    const [promoSnap, redemptionSnap, userSnap] = await Promise.all([
      tx.get(promoRef),
      tx.get(redemptionRef),
      tx.get(userRef)
    ]);

    if (!promoSnap.exists()) throw new Error('Invalid code');

    const promo = promoSnap.data();
    if (!promo.active) throw new Error('Code is inactive');
    if (promo.expiresAtMs && Date.now() > promo.expiresAtMs) throw new Error('Code has expired');
    if (redemptionSnap.exists()) throw new Error('You already redeemed this code');

    const maxUses = Number(promo.maxRedemptions || 0); // 0 = unlimited
    const used = Number(promo.redemptionCount || 0);
    if (maxUses > 0 && used >= maxUses) throw new Error('Code usage limit reached');

    const currentCredits = (userSnap.exists() ? (userSnap.data().credits || 0) : 0);
    const add = Number(promo.credits) || 0;
    if (add <= 0) throw new Error('Invalid code');

    // Write redemption record first
    tx.set(redemptionRef, {
      userId,
      code: normalized,
      creditsGranted: add,
      redeemedAt: serverTimestamp()
    });

    // Update user balance
    tx.update(userRef, {
      credits: (currentCredits || 0) + add,
      updatedAt: serverTimestamp()
    });

    // Increment global redemption counter
    tx.update(promoRef, {
      redemptionCount: used + 1
    });

    return { success: true, creditsAdded: add };
  });
}

export async function grantCreditsToUserByEmail(adminEmail, targetEmail, credits) {
  const usersCol = collection(db, 'users');
  const q = query(usersCol, where('email', '==', targetEmail));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('User not found');

  const userDocSnap = snap.docs[0];
  const userRef = doc(db, 'users', userDocSnap.id);
  const currentCredits = userDocSnap.data().credits || 0;
  await setDoc(userRef, { credits: currentCredits + Number(credits), updatedAt: serverTimestamp() }, { merge: true });
  return { success: true, userId: userDocSnap.id, newCredits: currentCredits + Number(credits) };
} 